import { MontuMessage } from "@repo/components/src/shared/chat/types";
import ModalProvider from "@repo/components/src/globalProviders/modal";
import ModalImage from "@repo/components/src/shared/chat/giftedChat/bubble/image/modalImage";
import MontuMarkdown from "@repo/components/src/shared/markdown";
import { Box } from "@repo/components/src/ui/box";
import { HStack } from "@repo/components/src/ui/hstack";
import { Icon } from "@repo/components/src/ui/icon";
import { Image } from "@repo/components/src/ui/image";
import { Pressable } from "@repo/components/src/ui/pressable";
import { Text } from "@repo/components/src/ui/text";
import { VStack } from "@repo/components/src/ui/vstack";
import { Check, CheckCheck, Clock9 } from "lucide-react-native";
import { memo, useCallback, useMemo } from "react";
import {
  BubbleProps,
  useChatContext,
  utils,
} from "@kesha-antonov/react-native-chat";
import AudioMessage from "./audio";
import FileAttachment from "./fileAttachment";
import PdfViewer from "./pdf";
import VideoMessage from "./video";

const { isSameUser } = utils;

const isSameDay = (
  current: { createdAt?: string | number | Date },
  diff: { createdAt?: string | number | Date } | null | undefined,
) => {
  if (!diff || !diff.createdAt || !current.createdAt) return false;
  const a = new Date(current.createdAt);
  const b = new Date(diff.createdAt);
  if (isNaN(a.getTime()) || isNaN(b.getTime())) return false;
  return (
    a.getFullYear() === b.getFullYear() &&
    a.getMonth() === b.getMonth() &&
    a.getDate() === b.getDate()
  );
};

const GiftedChatBubble = memo(function GiftedChatBubble(
  props: BubbleProps<MontuMessage>,
) {
  const { getLocale } = useChatContext();
  const isRight = props.position === "right";
  const hasMedia = Boolean(
    props.currentMessage.image ||
    props.currentMessage.video ||
    props.currentMessage.pdf ||
    props.currentMessage.audioFile ||
    props.currentMessage.file,
  );
  const isSameThread = useMemo(
    () =>
      isSameUser(props.currentMessage, props.previousMessage) &&
      isSameDay(props.currentMessage, props.previousMessage),
    [props.currentMessage, props.previousMessage],
  );

  const renderMessageImage = useCallback(() => {
    if (props.currentMessage && props.currentMessage.image) {
      const { containerStyle, wrapperStyle, ...messageImageProps } = props;
      if (props.renderMessageImage)
        return props.renderMessageImage(messageImageProps);
      const imageUri = props.currentMessage.image;
      return (
        <Pressable
          onPress={() =>
            ModalProvider.show({
              children: <ModalImage uri={imageUri} />,
              size: "full",
              contentProps: {
                className: "bg-black h-full border-0 p-0",
              },
              enableSwipeToDismiss: true,
            })
          }
        >
          {(pressableProps) => (
            <Box
              style={{ opacity: pressableProps.pressed ? 0.7 : 1 }}
              className="overflow-hidden rounded-2xl w-full min-w-64 max-w-sm"
            >
              <Image
                source={{ uri: imageUri }}
                size="none"
                alt="Image"
                className="rounded-2xl w-full aspect-video bg-background-900"
                contentFit="cover"
                contentPosition="center"
              />
            </Box>
          )}
        </Pressable>
      );
    }
    return null;
  }, [props]);

  const textColor = isRight ? "#ffffff" : undefined;
  const linkColor = isRight ? "#ffffff" : undefined;

  const renderMessageText = useCallback(() => {
    if (props.currentMessage.text) {
      if (props.renderMessageText) return props.renderMessageText(props);
      return (
        <Box
          style={{ opacity: props.currentMessage.sent ? 1 : 0.7 }}
          className={hasMedia ? "mt-1" : ""}
        >
          <MontuMarkdown baseSize={14} color={textColor} linkColor={linkColor}>
            {props.currentMessage.text}
          </MontuMarkdown>
        </Box>
      );
    }
    return null;
  }, [props, hasMedia, textColor, linkColor]);

  const renderMessagePdf = useCallback(() => {
    if (props.currentMessage.pdf) {
      return (
        <PdfViewer uri={props.currentMessage.pdf} position={props.position} />
      );
    }
    return null;
  }, [props]);

  const renderMessageVideo = useCallback(() => {
    if (props.currentMessage.video) {
      return (
        <VideoMessage
          uri={props.currentMessage.video}
          position={props.position}
        />
      );
    }
    return null;
  }, [props]);

  const renderMessageAudio = useCallback(() => {
    if (props.currentMessage.audioFile) {
      return (
        <AudioMessage
          uri={props.currentMessage.audioFile.uri}
          name={props.currentMessage.audioFile.name}
          position={props.position}
        />
      );
    }
    return null;
  }, [props]);

  const renderMessageFile = useCallback(() => {
    if (props.currentMessage.file) {
      return (
        <FileAttachment
          uri={props.currentMessage.file.uri}
          name={props.currentMessage.file.name}
          mimeType={props.currentMessage.file.mimeType}
          position={props.position}
        />
      );
    }
    return null;
  }, [props]);

  const renderTicks = useCallback(() => {
    if (!isRight) return null;
    if (!props.currentMessage) return null;
    let icon = null;
    if (props.currentMessage.pending) icon = Clock9;
    if (props.currentMessage.sent) icon = Check;
    if (props.currentMessage.received) icon = CheckCheck;
    if (!icon) return null;
    return (
      <Icon
        as={icon}
        size="2xs"
        className={
          props.currentMessage.messageRead
            ? "text-background-0"
            : "text-background-0/70"
        }
      />
    );
  }, [isRight, props.currentMessage]);

  const hasVisualMedia = Boolean(
    props.currentMessage.image || props.currentMessage.video,
  );
  const isMediaOnly = hasVisualMedia && !props.currentMessage.text;
  const timeLight = isRight || isMediaOnly;

  const renderTime = useCallback(() => {
    if (!props.currentMessage.createdAt) return null;
    if (props.renderTime) return props.renderTime(props);
    return (
      <HStack space="xs" className="items-center">
        <Text
          size="2xs"
          className={`tracking-wider ${
            timeLight ? "text-background-0/90" : "text-typography-400"
          }`}
        >
          {new Intl.DateTimeFormat(getLocale(), {
            timeStyle: "short",
          }).format(new Date(props.currentMessage.createdAt))}
        </Text>
        {renderTicks()}
      </HStack>
    );
  }, [props, getLocale, renderTicks, timeLight]);

  const bubbleOuterClass = useMemo(() => {
    if (isRight) {
      const base =
        "bg-gradient-to-br from-brand-500 to-brand-600 shadow-[0_10px_30px_-10px_rgba(124,58,237,0.55)]";
      return `${base} ${
        isSameThread ? "rounded-tr-xl" : "rounded-br-lg"
      } rounded-3xl`;
    }
    return `bg-background-0 border border-outline-100 ${
      isSameThread ? "rounded-tl-xl" : "rounded-bl-lg"
    } rounded-3xl shadow-[0_2px_8px_-3px_rgba(15,23,42,0.08)]`;
  }, [isRight, isSameThread]);

  const innerPaddingClass = isMediaOnly ? "p-1.5" : "px-3.5 py-2.5";

  return (
    <Box className={`shrink flex-1 ${bubbleOuterClass} overflow-hidden`}>
      {isRight && (
        <Box
          pointerEvents="none"
          className="absolute inset-0 opacity-50"
          style={{
            backgroundImage:
              "radial-gradient(120% 80% at 0% 0%, rgba(255,255,255,0.28) 0%, rgba(255,255,255,0) 55%), linear-gradient(150deg, rgba(255,255,255,0) 50%, rgba(0,0,0,0.12) 100%)",
          }}
        />
      )}
      <VStack className={innerPaddingClass} space="xs">
        {renderMessageImage()}
        {renderMessagePdf()}
        {renderMessageVideo()}
        {renderMessageAudio()}
        {renderMessageFile()}
        {renderMessageText()}
        <HStack
          className={`items-center ${
            isRight ? "justify-end" : "justify-start"
          } ${isMediaOnly ? "absolute bottom-2 right-3" : ""}`}
        >
          {isMediaOnly ? (
            <Box className="px-2 py-0.5 rounded-full bg-background-950/40 backdrop-blur-sm">
              {renderTime()}
            </Box>
          ) : (
            renderTime()
          )}
        </HStack>
      </VStack>
    </Box>
  );
});

export default GiftedChatBubble;
